// src/components/MuiSidebar.jsx
import React, { useState, useContext, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { styled, alpha } from "@mui/material/styles";
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  useMediaQuery,
  Switch,
  ListItemButton,
  useTheme,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  AccountBox as ProfileIcon,
  AccountBalance as ProjectsIcon,
  ExitToApp as LogoutIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../assets/Logo.png";
import LogoW from "../assets/LogoW.png";
import { ColorModeContext } from "../styles/ColorModeContext";
import axios from "axios";

const DRAWER_WIDTH = 250;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 2),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  minHeight: 48,
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: 40,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  ...(active && {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    borderRight: `3px solid ${theme.palette.primary.main}`,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  }),
}));

const StyledLogoutButton = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.5, 2),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.error.main,
  minHeight: 48,
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: 40,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.error.main, 0.08),
  },
}));

const MuiSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const mode = theme.palette.mode;

  // sublist state (we only populate when viewing a project detail)
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [subProjects, setSubProjects] = useState([]); // small array [{id, title}]
  const [loadingSub, setLoadingSub] = useState(false);
  const [subError, setSubError] = useState(null);

  // activeProjectId derived only for exact detail routes like /projects/:id
  const activeProjectId = (() => {
    const m = location.pathname.match(/^\/projects\/([^/]+)$/);
    return m ? m[1] : null;
  })();

  const menuItems = [
    { path: "/home", text: "Home", icon: <DashboardIcon /> },
    { path: "/projects", text: "Projects", icon: <ProjectsIcon /> },
  ];

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("user");
    } catch (e) {
      /* ignore */
    }
    window.location.replace("/");
  };

  const handleDrawerToggle = () => setMobileOpen((s) => !s);

  // Only show the project sublist when on a detail route (not on /projects list or home)
  const isProjectDetailRoute = /^\/projects\/[^/]+$/.test(location.pathname);

  const LIST_ENDPOINT = `${process.env.REACT_APP_API_URL || ""}/projects`;

  // Try to load a single pinned project (fast) using sessionStorage, else fetch single project title
  const loadPinnedProjectForDetail = useCallback(
    async (projectId) => {
      setLoadingSub(true);
      setSubError(null);

      // 1) try sessionStorage.lastOpenedProject
      try {
        const last = sessionStorage.getItem("lastOpenedProject");
        if (last) {
          const parsed = JSON.parse(last);
          if (
            parsed &&
            String(parsed.id) === String(projectId) &&
            parsed.title
          ) {
            setSubProjects([{ id: String(parsed.id), title: parsed.title }]);
            setProjectsOpen(true);
            setLoadingSub(false);
            try {
              sessionStorage.setItem(
                "projectsListCache",
                JSON.stringify([{ id: parsed.id, title: parsed.title }])
              );
            } catch { }
            return;
          }
        }
      } catch (e) {
        // ignore JSON parse errors and fall through
      }

      // 2) fallback to fetching the single project resource (lightweight)
      try {
        const token =
          localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const singleUrl = `${LIST_ENDPOINT.replace(/\/$/, "")}/${projectId}`;
        const resp = await axios.get(singleUrl, { headers });
        const raw = resp?.data;
        const dataObj = raw?.data ?? raw;
        const title =
          dataObj?.title ??
          dataObj?.name ??
          dataObj?.project_name ??
          dataObj?.project_title ??
          String(projectId);
        setSubProjects([{ id: String(projectId), title }]);
        setProjectsOpen(true);
        try {
          sessionStorage.setItem(
            "projectsListCache",
            JSON.stringify([{ id: String(projectId), title }])
          );
        } catch { }
      } catch (err) {
        console.warn("Failed to fetch single project for sidebar", err);
        setSubError("Could not load project");
        // fallback to cached list or lastOpenedProject
        const cached = sessionStorage.getItem("projectsListCache");
        if (cached) {
          try {
            setSubProjects(JSON.parse(cached));
          } catch {
            /* ignore */
          }
        } else {
          const last = sessionStorage.getItem("lastOpenedProject");
          if (last) {
            try {
              const parsed = JSON.parse(last);
              if (parsed?.id)
                setSubProjects([
                  { id: String(parsed.id), title: parsed.title || parsed.id },
                ]);
            } catch {
              /* ignore */
            }
          }
        }
      } finally {
        setLoadingSub(false);
      }
    },
    [LIST_ENDPOINT]
  );

  // Effect: load single pinned project only on detail route
  useEffect(() => {
    if (isProjectDetailRoute && activeProjectId) {
      loadPinnedProjectForDetail(activeProjectId);
    } else {
      // collapse & clear when not on detail route
      setProjectsOpen(false);
      setSubProjects([]);
      setLoadingSub(false);
      setSubError(null);
    }
  }, [
    isProjectDetailRoute,
    activeProjectId,
    loadPinnedProjectForDetail,
    location.pathname,
  ]);

  const drawerContent = (
    <>
      <LogoContainer>
        <img
          src={mode === "dark" ? LogoW : Logo}
          alt="Logo"
          style={{ width: 40, height: 40, borderRadius: "10%" }}
        />
        <Typography variant="h6" fontWeight={700}>
          PROPQUANT
        </Typography>
      </LogoContainer>

      <List sx={{ p: 0 }}>
        {menuItems.map((item) => {
          const isProjects = item.path === "/projects";
          const isActive =
            item.path === location.pathname ||
            (isProjects && location.pathname.startsWith("/projects"));

          return (
            <Box key={item.path}>
              {/* Projects row always navigates to /projects (Link) */}
              <StyledListItemButton
                component={Link}
                to={item.path}
                active={isActive ? 1 : 0}
                sx={{ display: "flex", alignItems: "center" }}
                onClick={(e) => {
                  // close mobile drawer on navigation
                  if (isMobile) handleDrawerToggle();

                  // If this is the Projects item, always navigate to /projects and collapse the sublist.
                  if (isProjects) {
                    // ensure we collapse any open sublist when going back to projects list
                    setProjectsOpen(false);

                    // If for some reason Link didn't perform (child stopped propagation), force navigation:
                    // (navigate is safe to call here; it will no-op if already at /projects)
                    navigate("/projects");
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />

                {isProjects && isProjectDetailRoute ? (
                  <IconButton
                    aria-label={
                      projectsOpen ? "Collapse projects" : "Expand projects"
                    }
                    onClick={(evt) => {
                      evt.preventDefault();
                      evt.stopPropagation(); // prevents the parent onClick (navigation) from firing
                      setProjectsOpen((s) => !s);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    {projectsOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                ) : null}
              </StyledListItemButton>

              {/* Projects sublist - unchanged, still toggles by projectsOpen */}
              {isProjects && (
                <Collapse in={projectsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {loadingSub && (
                      <Box sx={{ pl: 2, py: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}

                    {!loadingSub && subProjects.length === 0 && (
                      <Box sx={{ pl: 2, py: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {subError ? subError : "No project selected"}
                        </Typography>
                      </Box>
                    )}

                    {subProjects.map((p) => {
                      const active = String(p.id) === String(activeProjectId);
                      return (
                        <StyledListItemButton
                          key={p.id}
                          component="button"
                          onClick={() => {
                            navigate(`/projects/${p.id}`, {
                              state: { sidebarClickId: Date.now() },
                            });
                            if (isMobile) handleDrawerToggle();
                          }}
                          active={active ? 1 : 0}
                          sx={{ pl: 3 }}
                        >
                          <ListItemText
                            primary={p.title}
                            primaryTypographyProps={{ noWrap: true }}
                          />
                        </StyledListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <Box sx={{ marginTop: "auto", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ListItemIcon sx={{ pl: 1.5, minWidth: 40, color: "text.secondary" }}>
            {mode === "dark" ? <Brightness4Icon /> : <Brightness7Icon />}
          </ListItemIcon>
          <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
            {mode === "dark" ? "Dark Mode" : "Light Mode"}
          </Typography>
          <Switch
            edge="end"
            onChange={() => colorMode?.toggleColorMode?.()}
            checked={mode === "dark"}
          />
        </Box>

        <StyledListItemButton
          component={Link}
          to="/profile"
          active={location.pathname === "/profile" ? 1 : 0}
          onClick={() => isMobile && handleDrawerToggle()}
          sx={{ mt: 1 }}
        >
          <ListItemIcon>
            <ProfileIcon />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </StyledListItemButton>

        <Divider sx={{ my: 1 }} />

        <StyledLogoutButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </StyledLogoutButton>
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: "fixed",
            top: 12,
            left: 12,
            zIndex: 1301,
            width: 44,
            height: 44,
            bgcolor: "background.paper",
            color: "text.primary",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: (theme) => theme.shadows[4],
            backdropFilter: "blur(8px)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: "action.hover",
              transform: "scale(1.05)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              background: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.primary,
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
              backdropFilter: "blur(10px)",
              "& .MuiListItemIcon-root": { color: "inherit" },
            },
            "& .MuiBackdrop-root": {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          }}
          SlideProps={{
            timeout: { enter: 250, exit: 200 },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <StyledDrawer variant="permanent">{drawerContent}</StyledDrawer>
      )}
    </>
  );
};

export default MuiSidebar;
